import { g as getRoomType } from './get-room-type.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Hotel Link**
 *
 * @exports bs_283
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};

  /**
     * @memberOf module:bs_283
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {
      let lang;

      if (window.thankyouForm) {
        lang = window.thankyouForm.lang_current;
        if (lang === 'zht') {
          resolve('zh');
        } else if (lang && ctx.langs.test(lang)) {
          resolve(lang);
        }
        return;
      }

      lang = document.documentElement.lang;
      if (lang === 'vn') {
        resolve('vi');
        return;
      }

      reject('no_language');
    });
  };
  /**
  * @memberOf module:bs_283
  * @see {@link generate_search_url}
  *
  * @param {object} ctx - The context object.
  */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {

    // https://www.koorahotel.com/reservations/?checkin=15%2F05%2F2024&checkout=16%2F05%2F2024&filter_adult=2&filter_child=0&rooms=1
    const url = new URL(ctx.baseUrl);
    const validUrl = url.pathname.match(/reservations/);

    if (!validUrl) {
      ctx.newUrl = '';
      return ctx;
    }
    ctx.newUrl = url.toString();
    return ctx;
  };

  /**
     * @memberOf module:bs_283
     * @this thn.widgets.price
     * @see {@link Price#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceObserveDom = function priceObserveDom(ctx) {
    const container = document.querySelector('.room-available');

    if (container && !thn.hotelLinkObserver) {
      thn.hotelLinkObserver = thn.debounce(() => {
        ctx.widget.update();
      }, 500);
    }

    thn.observeDom(container, thn.hotelLinkObserver, {
      childList: true,
      subtree: true,
    });

    thn.pageUnload(() => {
      thn.stopObserveDom(container, thn.hotelLinkObserver);
      delete thn.hotelLinkObserver;
    });
  };

  /**
    *
    * @memberOf module:bs_283
    *
    */
  function roomDetailsParseData() {
    const roomElArray = [...document.querySelectorAll('[ng-init="list_plans = list_plan_current"]')];

    if (thn.empty(roomElArray)) {
      return {};
    }

    const roomsInfo = roomElArray
      .map((room) => {
        const roomName = room.querySelector('.room-type-name')?.textContent;
        const roomType = getRoomType(roomName);
        const roomAdults = thn.parseNumber(room.querySelector('.occupancy-wrapper')?.textContent);
        const roomRates = [...room.querySelectorAll('.rate-plan-wrapper')]
          .map((rate) => {
            const rateName = rate.querySelector('.rate-plan-name')?.textContent;
            const price = thn.parseNumber(rate.querySelector('.room-price-row')?.textContent);
            const breakfastRegex = /Breakfast|Bữa sáng|Frühstück|Ontbijt|Petit-déjeuner|아침식사 포함된/;
            const refundableRegex = /linh hoạt|Gratis|gratuite|Flexible|Free|靈活取消|Kostenfreie Stornierung|Hủy miễn phí|無料でキャンセル|무료 취소/;
            const hasBreakfast = !!rate.querySelector('.meal-plan')?.textContent?.match(breakfastRegex);
            const isRefundable = !!rate.querySelector('.cancellation-policy')?.textContent?.match(refundableRegex);
            const infoRate = {
              id: '',
              name: rateName,
              totalNight: price,
              breakfast: hasBreakfast,
              refundable: isRefundable,
            };

            return infoRate;
          });

        return {
          id: '',
          name: roomName,
          adults: roomAdults || 2,
          children: 0,
          type: roomType,
          prices: roomRates,
        };

      }).filter(Boolean);

    return roomsInfo;

  }

  /**
     * @memberOf module:bs_283
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {

    const widgetData = {};

    // Dates
    const dateBegin = document.querySelector('#check_in')?.value;
    const dateEnd = document.querySelector('#check_out')?.value;

    // Dates in value are always in english
    if (dateBegin && dateEnd) {
      widgetData.dateBegin = translateDate(dateBegin, 'en');
      widgetData.dateEnd = translateDate(dateEnd, 'en');
    }
    // Guests, Adults & Children
    widgetData.adults = thn.parseNumber(document.querySelector('[name=filter_adult]')?.value) || 2;
    widgetData.children = thn.parseNumber(document.querySelector('[name=filter_child]')?.value) || 0;
    widgetData.guests = widgetData.adults + widgetData.children;

    // Prices & Currency || No Rooms
    const prices = Array.from(document.querySelectorAll('.bws-inner')).filter((room) => room.querySelector('.room-selection')).map((room) => thn.parseNumber(room.querySelector('.room-price-row')?.textContent));
    const currency = document.querySelector('[name=currency]')?.value || 'USD';

    if (prices.length) {
      widgetData.price = Math.min(...prices);
      widgetData.currency = currency;
      widgetData.roomDetails = roomDetailsParseData();
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_283
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};
    const datalayer = window.dataLayer;
    const data = window.thankyouForm?.data;
    const bookingId = thn.objectGet(data, 'booking_number') || thn.objectGet(datalayer, 'id');

    if (!bookingId) {
      widgetData.set = false;
      return widgetData;
    }

    widgetData.bookingId = bookingId;

    // Dates
    const dateBeginData = thn.objectGet(data, 'check_in');
    const dateEndData = thn.objectGet(data, 'check_out');
    const dateBeginDl = thn.objectGet(datalayer, 'checkIn')?.replace(/-/g, '/');
    const dateEndDl = thn.objectGet(datalayer, 'checkOut')?.replace(/-/g, '/');

    if (dateBeginData && dateEndData) {
      widgetData.dateBegin = translateDate(dateBeginData, 'en');
      widgetData.dateEnd = translateDate(dateEndData, 'en');
    } else {
      widgetData.dateBegin = dateBeginDl;
      widgetData.dateEnd = dateEndDl;
    }

    // Price && currency
    const priceDl = thn.objectGet(datalayer, 'revenue');
    widgetData.price = thn.objectGet(data, 'Booking_total') || thn.objectGet(data, 'Card') || thn.objectGet(data, 'total_paid') || thn.parseNumber(document.querySelector('.rbk-price.right')?.textContent) || thn.parseNumber(priceDl);
    widgetData.currency = thn.objectGet(data, 'currency') || thn.objectGet(datalayer, 'currencyCode');

    return widgetData;
  };

  return engine;

}

export { factory };
